import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getPerdoruesPerPedagog } from '../../../components/common/common-functions/commonApiUrl/get-pedagogu-url';
import { getUsersUrl } from '../../../components/common/common-functions/commonApiUrl/get-users-url';
import getConfig from '../../../helpers/config';
import {
  ActivateTwoFA,
  ActivateTwoFAResponse,
  CreateStaffProps,
  Fjalekalimi,
  FjalekalimiUser,
  Generate2Fa,
  LoginData,
  LoginResponse,
  LogoutResponse,
  ParticipantsQueryType,
  Email,
  ChangePassword,
  SignUpData,
  UserData,
  UserForPedagogProps,
  UsersParams,
  VerifyOtpRequest,
  VerifyTwoFa,
  VerifyTwoFaResponse,
  editUserAttributes,
} from '../../../interfaces/Auth';
import { UpdateKlasaStudent } from '../../../interfaces/klasa-interface';
import { PaginationReturn } from '../../../interfaces/pagination';
import {
  Perdorues,
  PerdoruesPerPedagog,
} from '../../../interfaces/pedagoget-interface';
import { getPrepareHeaders } from '../get-prepare-headers';
import {
  CommonResponse,
  IdEntity,
} from '../../../interfaces/common-interfaces';

const { test } = getConfig();

export const authApi = createApi({
  reducerPath: 'auth',
  baseQuery: fetchBaseQuery({
    baseUrl: test.baseUrl + `/auth`,
    prepareHeaders: getPrepareHeaders,
  }),
  tagTypes: ['User', 'GetAllUsers', 'loggedInUser'],
  endpoints: builder => ({
    login: builder.mutation<LoginResponse, LoginData>({
      query: body => ({
        url: '/login',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    logout: builder.mutation<LogoutResponse, void>({
      query: () => ({
        url: '/logout',
        method: 'POST',
      }),
      invalidatesTags: [],
    }),
    signUp: builder.mutation<number, SignUpData>({
      query: body => ({
        url: '/signup',
        method: 'POST',
        body,
      }),
    }),
    createStaf: builder.mutation<number, CreateStaffProps>({
      query: body => ({
        url: '/perdorues/staf',
        method: 'POST',
        body,
      }),
    }),
    getAllUsers: builder.query<PaginationReturn<UserData>, UsersParams>({
      query: getUsersUrl,
      providesTags: ['GetAllUsers'],
    }),
    getSingleUser: builder.query<UserData, { id: number }>({
      query: id => ({
        url: `/perdorues/${id}`,
      }),
      providesTags: ['User'],
    }),
    activateUser: builder.mutation<{ id: number }, UsersParams>({
      query: data => ({
        url: 'aktivizo-perdorues',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['loggedInUser', 'GetAllUsers'],
    }),
    disableTfa: builder.mutation<{ id: number }, { id: number }>({
      query: ({ id }) => ({
        url: `disable-2fa/${id}`,
        method: 'PUT',
      }),
      invalidatesTags: ['GetAllUsers'],
    }),
    generateCodeTwoFa: builder.mutation<ActivateTwoFAResponse, Generate2Fa>({
      query: body => ({
        url: '/generate-qr-code/tfa',
        method: 'POST',
        body,
      }),
    }),
    activateTwoFa: builder.mutation<ActivateTwoFAResponse, ActivateTwoFA>({
      query: body => ({
        url: '/activate/tfa',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['loggedInUser'],
    }),
    verifyTwoFa: builder.mutation<VerifyTwoFaResponse, VerifyTwoFa>({
      query: body => ({
        url: '/verify/tfa',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['loggedInUser'],
    }),
    resetPassword: builder.mutation<number, Fjalekalimi>({
      query: body => ({
        url: '/ndrysho-fjalekalimin',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['GetAllUsers', 'loggedInUser'],
    }),
    getLoggedInUser: builder.query<UserData, void>({
      query: () => ({
        url: '/merr-perdoruesin-e-loguar',
      }),
      providesTags: ['loggedInUser'],
    }),
    merrPerdoruesPerPedagog: builder.query<
      PaginationReturn<PerdoruesPerPedagog>,
      UserForPedagogProps
    >({
      query: getPerdoruesPerPedagog,
    }),
    getUserForParticipants: builder.query<
      PaginationReturn<Perdorues>,
      ParticipantsQueryType
    >({
      query: args => {
        let url = `/perdorues/per-pjesemarres?page=${args.page}&sort=emriPersoni:asc`;

        if (args.size) {
          url += `&size=${args.size}`;
        }
        if (args.emriPersoni) {
          url += `&filter=emriPersoni:ilike:${args.emriPersoni}`;
        }
        return { url };
      },
    }),
    resetPasswordUser: builder.mutation<
      number,
      UpdateKlasaStudent<Partial<FjalekalimiUser>>
    >({
      query: ({ body, id }) => ({
        url: `/perdorues/reset-password/${id}/per-perdorues`,
        method: 'PUT',
        body: body,
      }),
      invalidatesTags: ['GetAllUsers', 'loggedInUser'],
    }),
    editUserAttributes: builder.mutation<
      number,
      UpdateKlasaStudent<Partial<editUserAttributes>>
    >({
      query: ({ body, id }) => ({
        url: `/perditeso-perdorues/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['GetAllUsers'],
    }),

    //Forgot password
    forgotPassword: builder.mutation<CommonResponse, Email>({
      query: ({ email }) => ({
        url: `/forgot-password?email=${email}`,
        method: 'POST',
      }),
    }),

    verifyOtp: builder.mutation<IdEntity, VerifyOtpRequest>({
      query: body => ({
        url: '/verify-otp',
        method: 'POST',
        body,
      }),
    }),

    resendCode: builder.mutation<CommonResponse, Email>({
      query: ({ email }) => ({
        url: `/resend-otp?email=${email}`,
        method: 'PUT',
      }),
    }),

    changePassword: builder.mutation<{ message: string }, ChangePassword>({
      query: body => ({
        url: '/change-password',
        method: 'PUT',
        body,
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useLogoutMutation,
  useSignUpMutation,
  useGetAllUsersQuery,
  useActivateUserMutation,
  useResetPasswordMutation,
  useGetSingleUserQuery,
  useGetLoggedInUserQuery,
  useLazyMerrPerdoruesPerPedagogQuery,
  useLazyGetUserForParticipantsQuery,
  useResetPasswordUserMutation,
  useEditUserAttributesMutation,
  useCreateStafMutation,
  useActivateTwoFaMutation,
  useVerifyTwoFaMutation,
  useGenerateCodeTwoFaMutation,
  useDisableTfaMutation,
  useForgotPasswordMutation,
  useVerifyOtpMutation,
  useResendCodeMutation,
  useChangePasswordMutation,
} = authApi;
